import React from 'react';
import { Box, TextField, Typography, RadioGroup, FormControlLabel, Radio, Paper, InputAdornment, FormLabel, Switch } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import InfoTooltip from '../../InfoTooltip';

const sectionPaperSx = {
  mb: 3, p: 2,
  border: '2px solid',
  borderColor: 'divider',
  borderRadius: 2,
  background: 'background.default',
} as const;

const sectionTitleSx = {
  fontWeight: 700, mb: 1.5,
  color: (theme: any) => theme.palette.primary.main,
} as const;

const tooltipSx = (theme: any) => ({
  bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
  color: theme.palette.mode === 'dark' ? '#222' : '#fff',
  fontSize: 15,
  borderRadius: 2,
  boxShadow: 3,
  px: 2,
  py: 1,
  fontWeight: 400,
  minWidth: 320,
});

interface PDBRelocationSettingsProps {
  sourcePdb: string;
  setSourcePdb: (v: string) => void;
  targetCdb: string;
  setTargetCdb: (v: string) => void;
  targetPdbName: string;
  setTargetPdbName: (v: string) => void;
  dbLink: string;
  setDbLink: (v: string) => void;
  parallelPdbCreation: string;
  setParallelPdbCreation: (v: string) => void;
  dropDbLink: boolean;
  setDropDbLink: (v: boolean) => void;
  refreshOption: string;
  setRefreshOption: (v: string) => void;
  refreshInterval: string;
  setRefreshInterval: (v: string) => void;
  sourcePath: string;
  setSourcePath: (v: string) => void;
  targetPath: string;
  setTargetPath: (v: string) => void;
  fileNameConvertOption: string;
  setFileNameConvertOption: (v: string) => void;
  platform: string;
  startTimeType?: 'now' | 'delay' | 'datetime';
  setStartTimeType?: (v: 'now' | 'delay' | 'datetime') => void;
  startTimeValue?: string;
  setStartTimeValue?: (v: string) => void;
  sid: string;
}

const PDBRelocationSettings: React.FC<PDBRelocationSettingsProps> = ({
  sourcePdb, setSourcePdb,
  targetCdb, setTargetCdb,
  targetPdbName, setTargetPdbName,
  dbLink, setDbLink,
  parallelPdbCreation, setParallelPdbCreation,
  dropDbLink, setDropDbLink,
  refreshOption, setRefreshOption,
  refreshInterval, setRefreshInterval,
  sourcePath, setSourcePath,
  targetPath, setTargetPath,
  fileNameConvertOption, setFileNameConvertOption,
  platform,
  startTimeType,
  setStartTimeType,
  startTimeValue,
  setStartTimeValue,
  sid,
}) => {
  const [dateWarning, setDateWarning] = React.useState('');
  const [userEditedStartTime, setUserEditedStartTime] = React.useState(false);
  const effectiveStartTimeType = startTimeType ?? 'now';

  function formatDateTimeInput(raw: string) {
    const v = raw.replace(/\D/g, '').slice(0, 14);
    let out = '';
    if (v.length > 0) out += v.slice(0, 2);
    if (v.length > 2) out += '/' + v.slice(2, 4);
    if (v.length > 4) out += '/' + v.slice(4, 8);
    if (v.length > 8) out += ' ' + v.slice(8, 10);
    if (v.length > 10) out += ':' + v.slice(10, 12);
    if (v.length > 12) out += ':' + v.slice(12, 14);
    return out;
  }

  React.useEffect(() => {
    if (effectiveStartTimeType === 'datetime' && startTimeValue && startTimeValue.length === 14) {
      const d = startTimeValue;
      const inputDate = new Date(
        Number(d.slice(4, 8)),
        Number(d.slice(2, 4)) - 1,
        Number(d.slice(0, 2)),
        Number(d.slice(8, 10)),
        Number(d.slice(10, 12)),
        Number(d.slice(12, 14))
      );
      if (inputDate.getTime() < Date.now()) {
        setDateWarning('Warning: Date cannot be in the past.');
      } else {
        setDateWarning('');
      }
    } else {
      setDateWarning('');
    }
  }, [effectiveStartTimeType, startTimeValue]);

  React.useEffect(() => {
    if (
      refreshOption === 'interval' &&
      Number(refreshInterval) > 0 &&
      setStartTimeType && setStartTimeValue &&
      !userEditedStartTime &&
      (startTimeType === undefined || startTimeType === 'delay' || startTimeType === 'now')
    ) {
      const currentMinutes = startTimeValue && /^\+?(\d+)m?$/.test(startTimeValue)
        ? parseInt((startTimeValue.match(/\d+/) || ['0'])[0], 10)
        : 0;
      if (!startTimeValue || currentMinutes < Number(refreshInterval)) {
        setStartTimeType('delay');
        setStartTimeValue(`+${refreshInterval}m`);
      }
    }
  }, [refreshOption, refreshInterval, setStartTimeType, setStartTimeValue, startTimeType, startTimeValue, userEditedStartTime]);

  React.useEffect(() => {
    setUserEditedStartTime(false);
  }, [refreshOption]);

  return (
    <Paper elevation={0} sx={{ p: 3, background: 'background.paper', borderRadius: 3 }}>

      {/* PDB / CDB / DB Link */}
      <Paper elevation={0} sx={sectionPaperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>PDB / CDB / DB Link</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Source PDB:</Typography>
          <TextField
            fullWidth size="small" value={sourcePdb}
            onChange={e => setSourcePdb(e.target.value)}
            placeholder={platform === 'WINDOWS.X64' ? 'PDB1_SRC' : 'pdb1_src'}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Target CDB:</Typography>
          <TextField
            fullWidth size="small" value={targetCdb}
            onChange={e => setTargetCdb(e.target.value)}
            placeholder={platform === 'WINDOWS.X64' ? 'CDB19_TGT' : 'cdb19_tgt'}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Target PDB Name:</Typography>
          <TextField
            fullWidth size="small" value={targetPdbName}
            onChange={e => setTargetPdbName(e.target.value)}
            placeholder={platform === 'WINDOWS.X64' ? 'PDB1_TGT' : 'pdb1_tgt'}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>DB Link:</Typography>
          <TextField
            fullWidth size="small" value={dbLink}
            onChange={e => setDbLink(e.target.value)}
            placeholder="db19_link"
          />
        </Box>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Limits the number of parallel processes used by the CDB to make the initial copy." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
            <Typography sx={{ fontWeight: 600 }}>Parallel PDB Creation:</Typography>
          </Tooltip>
          <TextField
            type="number"
            size="small"
            value={parallelPdbCreation}
            onChange={e => setParallelPdbCreation(e.target.value)}
            placeholder="e.g. 2"
            sx={{ width: 150, ml: 2 }}
            inputProps={{ min: 1, style: { colorScheme: 'dark' } }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={dropDbLink}
            onChange={e => setDropDbLink(e.target.checked)}
            color="primary"
            size="small"
          />
          <Tooltip title="Instructs AutoUpgrade to drop the database link after the upgrade/relocation and when it's no longer needed." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
            <Typography sx={{ fontWeight: 600, ml: 1 }}>Drop DB Link</Typography>
          </Tooltip>
        </Box>
      </Paper>

      {/* Refresh Options / Start Time */}
      <Paper elevation={0} sx={sectionPaperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Refresh Options</Typography>
        <Box sx={{ display: 'flex', gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <RadioGroup
              value={refreshOption}
              onChange={e => setRefreshOption(e.target.value)}
              sx={{
                '.MuiFormControlLabel-root': { mb: 1.5, alignItems: 'center', display: 'flex' },
                '.MuiSvgIcon-root': { fontSize: 20, ml: 0.5, color: 'primary.main' },
              }}
            >
              <FormControlLabel
                value="interval"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600 }}>Refresh Interval (in minutes):</span>
                    <TextField
                      type="number"
                      size="small"
                      value={refreshInterval}
                      onChange={e => setRefreshInterval(String(e.target.value))}
                      sx={{ width: 150, ml: 2 }}
                      inputProps={{ min: 1, style: { colorScheme: 'dark' } }}
                      InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontWeight: 600, fontSize: 14 }}>min</Typography></InputAdornment> }}
                      disabled={refreshOption !== 'interval'}
                    />
                  </Box>
                }
              />
              <FormControlLabel
                value="clone_only"
                control={<Radio />}
                label={<span style={{ fontWeight: 600 }}>None (CLONE_ONLY)</span>}
              />
            </RadioGroup>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 600, display: 'inline' }}>Start Time:</Typography>
              <InfoTooltip
                title="Sets a future start time for the upgrade job to run. Use this parameter to schedule upgrade jobs to balance the load on your server, and to prevent multiple jobs from starting immediately."
                placement="right"
              />
            </FormLabel>
            <RadioGroup
              value={effectiveStartTimeType}
              onChange={e => {
                const newType = e.target.value as 'now' | 'delay' | 'datetime';
                setUserEditedStartTime(true);
                (setStartTimeType || (() => {}))(newType);
                if ((setStartTimeValue || (() => {})) && (newType === 'delay' || newType === 'datetime')) {
                  (setStartTimeValue || (() => {}))('');
                }
              }}
            >
              <FormControlLabel value="now" control={<Radio />} label="Now" />
              <FormControlLabel value="delay" control={<Radio />} label={'+XhYm'} />
              <FormControlLabel value="datetime" control={<Radio />} label="dd/mm/yyyy hh:mm:ss" />
            </RadioGroup>
            {effectiveStartTimeType === 'delay' && (
              <TextField
                size="small"
                label="Delay (e.g. +2h30m)"
                value={startTimeValue}
                onChange={e => {
                  setUserEditedStartTime(true);
                  setStartTimeValue && setStartTimeValue(e.target.value);
                }}
                placeholder="+1h0m"
                sx={{ mt: 1, width: '100%' }}
              />
            )}
            {effectiveStartTimeType === 'datetime' && (
              <>
                <TextField
                  size="small"
                  label="Date & Time (dd/mm/yyyy hh:mm:ss)"
                  value={formatDateTimeInput(startTimeValue || '')}
                  onChange={e => {
                    setUserEditedStartTime(true);
                    const raw = e.target.value.replace(/\D/g, '');
                    setStartTimeValue && setStartTimeValue(raw);
                  }}
                  placeholder="10/05/2025 14:30:00"
                  sx={{ mt: 1, width: '100%' }}
                  error={!!dateWarning}
                />
                {dateWarning && (
                  <Typography color="error" sx={{ mt: 1, fontSize: 14 }}>{dateWarning}</Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* File Name Convert / Copy Option */}
      <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 0 }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>File Name Convert / Copy Option</Typography>
        <RadioGroup
          value={fileNameConvertOption}
          onChange={e => setFileNameConvertOption(e.target.value)}
          sx={{
            '.MuiFormControlLabel-root': { mb: 1.5, alignItems: 'center', display: 'flex' },
            '.MuiSvgIcon-root': { fontSize: 20, ml: 0.5, color: 'primary.main' },
          }}
        >
          <FormControlLabel
            value="convert"
            control={<Radio />}
            label={
              <Tooltip
                title="file_name_convert will be used directly in AutoUpgrade config file. You cannot use this option if you have OMF files."
                placement="right" arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <Typography sx={{ fontWeight: 600 }}>Use Source/Target Paths</Typography>
              </Tooltip>
            }
          />
          <FormControlLabel
            value="none"
            control={<Radio />}
            label={
              <Tooltip
                title="Required to set DB_CREATE_FILE_DEST or PDB_FILE_NAME_CONVERT on Target CDB"
                placement="right" arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <Typography sx={{ fontWeight: 600 }}>NONE</Typography>
              </Tooltip>
            }
          />
        </RadioGroup>

        {fileNameConvertOption === 'convert' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Source Path:</Typography>
              <TextField
                fullWidth size="small" value={sourcePath}
                onChange={e => setSourcePath(e.target.value)}
                placeholder={
                  platform === 'WINDOWS.X64'
                    ? `${(sid || 'DB1').toUpperCase()}\\${(sourcePdb || 'PDB1_SRC').toUpperCase()}`
                    : `${sid !== '' ? sid : 'DB1'}/${sourcePdb || 'pdb1_src'}`
                }
                sx={{ mt: 1, mb: 2 }}
                InputProps={{
                  sx: {
                    '&::placeholder': { color: '#b0b0b0', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: sourcePath === '' ? '#b0b0b0' : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Target Path:</Typography>
              <TextField
                fullWidth size="small" value={targetPath}
                onChange={e => setTargetPath(e.target.value)}
                placeholder={
                  platform === 'WINDOWS.X64'
                    ? `${(targetCdb || 'CDB19_TGT').toUpperCase()}\\${(targetPdbName || 'PDB1_TGT').toUpperCase()}`
                    : `${targetCdb !== '' ? targetCdb : 'cdb19_tgt'}/${targetPdbName || 'pdb1_tgt'}`
                }
                sx={{ mt: 1 }}
                InputProps={{
                  sx: {
                    '&::placeholder': { color: '#b0b0b0', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: targetPath === '' ? '#b0b0b0' : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Paper>
  );
};

export default PDBRelocationSettings;
